import { useEffect, useMemo, useRef } from 'react';
import { Video, X } from 'lucide-react';
import { toast } from 'sonner';
import { SUPPORT_TICKET_VIDEO_MAX_BYTES, SUPPORT_TICKET_VIDEO_MAX_MB } from '@/services/supportTicketApi';

interface TicketVideoPickerProps {
  video: File | null;
  onChange: (file: File | null) => void;
  className?: string;
}

const ACCEPT = 'video/mp4,video/webm,video/quicktime,video/x-matroska,video/3gpp,video/x-msvideo,.mp4,.webm,.mov,.mkv,.3gp,.avi,.m4v';

const formatMb = (bytes: number) => `${(bytes / (1024 * 1024)).toFixed(1)} MB`;

/**
 * Single screen-recording picker for a support ticket or reply. Enforces the
 * 100 MB ceiling client-side so the operator gets an instant message instead
 * of waiting for a multi-minute upload to be rejected.
 */
export default function TicketVideoPicker({ video, onChange, className = '' }: TicketVideoPickerProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const previewUrl = useMemo(() => (video ? URL.createObjectURL(video) : null), [video]);

  useEffect(() => () => { if (previewUrl) URL.revokeObjectURL(previewUrl); }, [previewUrl]);

  const handleSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    if (inputRef.current) inputRef.current.value = '';
    if (!file) return;
    if (file.size > SUPPORT_TICKET_VIDEO_MAX_BYTES) {
      toast.error(`Video must be under ${SUPPORT_TICKET_VIDEO_MAX_MB} MB (selected ${formatMb(file.size)})`);
      return;
    }
    onChange(file);
  };

  if (video && previewUrl) {
    return (
      <div className={`flex items-start gap-3 ${className}`}>
        <video src={previewUrl} controls preload="metadata" className="h-24 w-40 rounded-lg border border-gray-200 bg-black object-contain" />
        <div className="min-w-0 flex-1">
          <p className="text-sm text-gray-800 truncate">{video.name}</p>
          <p className="text-xs text-gray-500">{formatMb(video.size)}</p>
          <button
            type="button"
            onClick={() => onChange(null)}
            className="mt-1 inline-flex items-center gap-1 text-xs text-red-600 hover:text-red-700"
          >
            <X className="h-3 w-3" /> Remove video
          </button>
        </div>
      </div>
    );
  }

  return (
    <label className={`inline-flex items-center gap-2 px-3 py-2 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer text-sm text-gray-600 hover:border-indigo-400 hover:bg-indigo-50 transition-colors ${className}`}>
      <Video className="h-4 w-4 text-gray-400" />
      Add video <span className="text-xs text-gray-400">(max {SUPPORT_TICKET_VIDEO_MAX_MB} MB)</span>
      <input ref={inputRef} type="file" accept={ACCEPT} onChange={handleSelect} className="hidden" />
    </label>
  );
}
